import React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { Row, Col, Label } from 'reactstrap';
import moment from 'moment';
import CustomSelect from 'components/CustomSelect';
import { formResMessage } from 'utils/commonFunc';
import {
  fetchBusAttendanceDailyApi,
  fetchBusAttendanceWeeklyApi,
  fetchBusAttendanceMonthlyApi,
} from 'actions/admin/transport';
import ReportTable from './ReportTable';
import { buildAttendanceReport } from './transportReports';

const PERIODS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly (Mon - Sun)' },
  { value: 'monthly', label: 'Monthly' },
];
const ALL = '';
const TRIP_OPTIONS = [
  { value: ALL, label: 'Pickup & Drop' },
  { value: 'PICKUP', label: 'Pickup' },
  { value: 'DROP', label: 'Drop' },
];
const STATUS_OPTIONS = [
  { value: ALL, label: 'All statuses' },
  { value: 'PICKED', label: 'Picked up' },
  { value: 'DROPPED', label: 'Dropped' },
  { value: 'ABSENT', label: 'Absent' },
];

// Bus attendance marked by the drivers (pickup / drop / absent): a day, a week or a month at a time, narrowed
// by route, trip and status. The week and month come with the backend's own totals; everything is also totalled
// here for the filtered view so the tiles always match the table.
const AttendanceReport = ({ schoolId, batchId, loggedUserToken, showWarning, busesById, driversById }) => {
  const [period, setPeriod] = useState('daily');
  const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
  const [month, setMonth] = useState(moment().format('YYYY-MM'));
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [routeFilter, setRouteFilter] = useState(ALL);
  const [tripFilter, setTripFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);

  useEffect(() => {
    if (!batchId) return;
    let cancelled = false;
    setLoading(true);
    const base = { schoolId, batchId, token: loggedUserToken };
    const request =
      period === 'daily'
        ? fetchBusAttendanceDailyApi({ ...base, date })
        : period === 'weekly'
        ? fetchBusAttendanceWeeklyApi({ ...base, date })
        : fetchBusAttendanceMonthlyApi({ ...base, year: month.slice(0, 4), month: Number(month.slice(5, 7)) });
    request
      .then(res => !cancelled && setRecords(period === 'daily' ? res.data || [] : res.data?.records || []))
      .catch(err => {
        if (cancelled) return;
        setRecords([]);
        showWarning(formResMessage(err), 'error');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [period, date, month, batchId]);

  const report = useMemo(
    () => buildAttendanceReport({ records, busesById, driversById }),
    [records, busesById, driversById]
  );
  const routeOptions = useMemo(
    () => [
      { value: ALL, label: 'All routes' },
      ...[...new Set(report.rows.map(row => row.raw.routeName).filter(Boolean))]
        .sort()
        .map(r => ({ value: r, label: r })),
    ],
    [report.rows]
  );
  const rows = report.rows.filter(
    row =>
      (!routeFilter || row.raw.routeName === routeFilter) &&
      (!tripFilter || row.raw.tripType === tripFilter) &&
      (!statusFilter || row.raw.status === statusFilter)
  );
  const count = status => rows.filter(row => row.raw.status === status).length;

  const filters = (
    <Row>
      <Col lg={3} md={4} sm={6} xs={12} className="mb-2">
        <Label className="form-label">Period</Label>
        <CustomSelect
          noLabel
          usePortal
          name="period"
          value={period}
          options={PERIODS}
          onChange={(n, v) => setPeriod(v)}
        />
      </Col>
      <Col lg={3} md={4} sm={6} xs={12} className="mb-2">
        <Label className="form-label">
          {period === 'monthly' ? 'Month' : period === 'weekly' ? 'Any day of the week' : 'Date'}
        </Label>
        {period === 'monthly' ? (
          <input
            className="form-control"
            style={{ height: 38 }}
            type="month"
            value={month}
            onChange={e => e.target.value && setMonth(e.target.value)}
          />
        ) : (
          <input
            className="form-control"
            style={{ height: 38 }}
            type="date"
            value={date}
            onChange={e => e.target.value && setDate(e.target.value)}
          />
        )}
      </Col>
      <Col lg={2} md={4} sm={6} xs={12} className="mb-2">
        <Label className="form-label">Route</Label>
        <CustomSelect
          noLabel
          usePortal
          name="route"
          value={routeFilter}
          options={routeOptions}
          onChange={(n, v) => setRouteFilter(v)}
        />
      </Col>
      <Col lg={2} md={4} sm={6} xs={12} className="mb-2">
        <Label className="form-label">Trip</Label>
        <CustomSelect
          noLabel
          usePortal
          name="trip"
          value={tripFilter}
          options={TRIP_OPTIONS}
          onChange={(n, v) => setTripFilter(v)}
        />
      </Col>
      <Col lg={2} md={4} sm={6} xs={12} className="mb-2">
        <Label className="form-label">Status</Label>
        <CustomSelect
          noLabel
          usePortal
          name="status"
          value={statusFilter}
          options={STATUS_OPTIONS}
          onChange={(n, v) => setStatusFilter(v)}
        />
      </Col>
    </Row>
  );

  return (
    <ReportTable
      title="Bus attendance"
      fileName={`bus_attendance_${period}`}
      columns={report.columns}
      rows={rows}
      filters={filters}
      summary={[
        { label: 'Records', value: rows.length },
        { label: 'Picked up', value: count('PICKED') },
        { label: 'Dropped', value: count('DROPPED') },
        { label: 'Absent', value: count('ABSENT'), tone: 'bad' },
      ]}
      emptyText="No attendance was marked for this period."
      loading={loading}
    />
  );
};

export default AttendanceReport;
